export interface ScreeningItem {
  name: string;
  result: string;
  status: 'completed' | 'due' | 'recommended' | 'not-recommended';
}

export interface VitalSign {
  label: string;
  value: string;
  flag?: 'elevated' | 'normal';
}

export interface VitalsRow {
  date: string;
  vitals: VitalSign[];
}

export interface SocialHistoryItem {
  category: string;
  value: string;
}

export interface Allergy {
  drug: string;
  food: string;
  environmental: string;
}

export interface Document {
  type: string;
  details: string;
  date: string;
  file?: string;
}

export interface Diagnosis {
  code: string;
  description: string;
}

export interface Encounter {
  date: string;
  type: string;
  details: string;
}

export interface PastMedicalHistory {
  majorEvents: string[];
  preventiveCare: string[];
}

export interface VisitTemplate {
  id: string;
  visitType: string;
  cptCode: string;
  patientName: string;
  age: number;
  sex: 'M' | 'F';
  dob: string;
  chiefComplaint: string;
  visitDate: string;
  medications: { name: string; sig: string; status: 'active' | 'new' | 'sent'; startDate?: string }[];
  subjective: string;
  objective: string;
  assessmentLines: string[];
  icdCodes: Diagnosis[];
  planItems: string[];
  orderedAssessments: string[];
  qualityFlags: string[];
  screening?: ScreeningItem[];
  vitals?: VitalsRow[];
  socialHistory?: SocialHistoryItem[];
  allergies?: Allergy;
  documents?: Document[];
  diagnoses?: Diagnosis[];
  encounters?: Encounter[];
  familyHistory?: string;
  pastMedicalHistory?: PastMedicalHistory;
}

export const AVAILABLE_ASSESSMENTS: string[] = [
  'PHQ-2',
  'PHQ-9',
  'BMI',
  'GAD-7',
  'Cervical Cancer Screening (Pap)',
  'Tobacco Screening',
  'Alcohol (AUDIT-C)',
  'A1C',
  'CBC',
  'CMP',
  'Lipid Panel',
  'TSH',
  'Chest X-ray Review',
  'SDOH Screening',
  'Medication Reconciliation',
  'Allergy Reconciliation',
  'Immunization Review',
  'Labs Review',
];

export const templates: VisitTemplate[] = [
  {
    id: 'elena-ramirez-well',
    visitType: 'FM – Well Visit – Annual + Follow-up',
    cptCode: '99396',
    patientName: 'Elena Ramirez',
    age: 48,
    sex: 'F',
    dob: '04/22/1977',
    chiefComplaint: 'well visit (Appt time: 2/26/2026 10:00:00 AM) (Arrival time: 10:04 AM)',
    visitDate: '02/26/2026',
    vitals: [
      {
        date: '02/26/2026',
        vitals: [
          { label: 'Height', value: '62 in' },
          { label: 'Weight', value: '218 lb', flag: 'elevated' },
          { label: 'BMI', value: '39.87', flag: 'elevated' },
          { label: 'BP', value: '145/88 mmHg', flag: 'elevated' },
          { label: 'Temp', value: '98°F' },
          { label: 'Pulse', value: '75 bpm' },
          { label: 'RR', value: '14 bpm' },
        ],
      },
      {
        date: '07/14/2022',
        vitals: [
          { label: 'Height', value: '62 in' },
          { label: 'Weight', value: '189 lb' },
          { label: 'BMI', value: '34.57', flag: 'elevated' },
          { label: 'BP', value: '128/71 mmHg' },
          { label: 'Temp', value: '98.2°F' },
          { label: 'Pulse', value: '72 bpm' },
          { label: 'RR', value: '14 bpm' },
        ],
      },
      {
        date: '04/14/2022',
        vitals: [
          { label: 'Height', value: '62 in' },
          { label: 'Weight', value: '171 lb' },
          { label: 'BMI', value: '31.28', flag: 'elevated' },
          { label: 'BP', value: '130/80 mmHg' },
          { label: 'Temp', value: '98°F' },
          { label: 'Pulse', value: '70 bpm' },
          { label: 'RR', value: '16 bpm' },
        ],
      },
    ],
    socialHistory: [
      { category: 'Tobacco use', value: 'Non-smoker 04/14/2022' },
      { category: 'Alcohol use', value: 'No alcohol use recorded' },
      { category: 'Social history (free text)', value: 'job; walmart / no alcohol' },
      { category: 'Gender identity', value: 'Female' },
      { category: 'Sexual orientation', value: 'Straight or heterosexual' },
      { category: 'Nutrition history', value: 'No nutrition history recorded' },
      { category: 'Pregnancy status', value: 'No pregnancy status recorded' },
      { category: 'Pregnancy intent', value: 'No pregnancy intent recorded' },
      { category: 'Food insecurity risk - HVS', value: 'No food insecurity risk recorded' },
    ],
    medications: [
      { name: 'Ergocalciferol 1.25 MG (50000 UT) Oral Capsule', sig: '1 cap PO weekly', status: 'new', startDate: '02/28/2026' },
      { name: 'Metoprolol Succinate ER 25 MG Oral Tablet', sig: '1 tab PO daily', status: 'new', startDate: '02/26/2026' },
      { name: 'Albuterol Sulfate HFA 108 MCG', sig: '2 puffs inhaled q6h PRN', status: 'active', startDate: '07/14/2022' },
      { name: 'Norethindrone Acet & Eth Estra (Junel 1/20)', sig: 'PO daily', status: 'active' },
    ],
    subjective: '',
    objective:
      'General: Normotensive, in no acute distress.\n' +
      'Head: Normocephalic, no lesions.\n' +
      'Eyes: PERRLA, EOM\'s full, conjunctivae clear, fundi grossly normal.\n' +
      'Ears: EAC\'s clear, TM\'s normal.\n' +
      'Nose: Mucosa normal, no obstruction.\n' +
      'Throat: Clear, no exudates, no lesions.\n' +
      'Neck: Supple, no masses, no thyromegaly, no bruits.\n' +
      'Chest: Lungs clear, no rales, no rhonchi, no wheezes.\n' +
      'Heart: RR, no murmurs, no rubs, no gallops.\n' +
      'Abdomen: Soft, no tenderness, no masses, BS normal.',
    assessmentLines: [],
    icdCodes: [],
    planItems: [],
    orderedAssessments: [],
    qualityFlags: [
      'Diagnoses reconciliation completed: Yes',
      'Medication allergy reconciliation completed: Yes',
      'Medication reconciliation completed: Yes',
      'Documentation of current medications: Yes',
    ],
    screening: [
      { name: 'Mammogram', result: 'Recommended (due 2026)', status: 'recommended' },
      { name: 'Hypertension education', result: 'Positive (based on vitals)', status: 'recommended' },
      { name: 'BMI', result: 'Positive (based on vitals)', status: 'recommended' },
      { name: 'Cervical cancer screening', result: 'Results documented and reviewed', status: 'completed' },
      { name: 'Colorectal cancer screening', result: 'Results documented and reviewed', status: 'completed' },
      { name: 'PHQ-2 questionnaire', result: 'Score 0', status: 'completed' },
      { name: 'Colonoscopy (procedure)', result: 'Results documented and reviewed', status: 'completed' },
    ],
    allergies: {
      drug: 'Patient has no known drug allergies (NKDA)',
      food: 'No food allergies recorded',
      environmental: 'No environmental allergies recorded',
    },
    documents: [
      { type: 'Lab results', details: 'Lipid Screen, CBC, Vitamin D, CMP, TSH, A1C, Urine Drug Screen, UA', date: '02/26/2025', file: '/documents/Claryn_Demo_LabResults_Elena_2025.pdf' },
      { type: 'Lab results', details: 'Lipid Screen, CBC, Iron/Ferritin, CMP, TSH, A1C, Vitamin D, UA, Urine Drug Screen', date: '04/14/2022', file: '/documents/Claryn_Demo_LabResults_Elena_2022.pdf' },
      { type: 'Imaging', details: 'US Breast Bilateral Complete', date: '08/15/2022', file: '/documents/Claryn_Demo_BreastUS_Elena.pdf' },
      { type: 'Imaging', details: 'Transabdominal Pelvic report', date: '08/15/2022', file: '/documents/Claryn_Demo_PelvicReport.pdf' },
      { type: 'Procedure', details: 'Colonoscopy — diverticulosis noted', date: '2021', file: '/documents/Claryn_Demo_Colonoscopy_Elena.pdf' },
      { type: 'Referral', details: 'Referral to Sleep Medicine', date: '02/26/2026' },
    ],
    diagnoses: [
      { code: 'J45.20', description: 'Mild intermittent asthma, uncomplicated' },
      { code: 'E66.01', description: 'Morbid obesity due to excess calories' },
    ],
    encounters: [
      { date: '02/26/2026', type: 'Well Visit', details: 'FM – Well Visit – Annual + Follow-up' },
      { date: '07/14/2022', type: 'Office Visit', details: 'Follow-up — asthma, weight management' },
      { date: '04/14/2022', type: 'Well Visit', details: 'FM – Annual Well Visit' },
    ],
    familyHistory: 'Parents; healthy',
    pastMedicalHistory: {
      majorEvents: [
        'NSVD x 2',
        'C-section x 1',
        'Liposuction and breast implants',
      ],
      preventiveCare: [
        'Well visit 4/2022 and 2/2026',
        'Pap 2023',
        'COVID vaccine — had',
      ],
    },
  },
  {
    id: 'fm-sick-rash',
    visitType: 'FM – Sick Visit – Rash',
    cptCode: '99213',
    patientName: 'Dorothy Mitchell',
    age: 70,
    sex: 'F',
    dob: '06/14/1955',
    chiefComplaint: 'rash on upper chest',
    visitDate: '12/29/2025',
    medications: [
      { name: 'Clotrimazole (Topical) (Clotrimazole) 1 % External Cream', sig: 'Apply to affected area as directed', status: 'sent' },
    ],
    subjective:
      'semi and labs rash upper chest x 1 week. mild itching.not spreading. sweats a lot. constipation from the medication. all good',
    objective:
      'General: Normotensive, in no acute distress. Head: Normocephalic, no lesions. Eyes: PERRLA, EOM\'s full, conjunctivae clear, fundi grossly normal. Ears: EAC\'s clear, TM\'s normal. Nose: Mucosa normal, no obstruction. Throat: Clear, no exudates, no lesions. Neck: Supple, no masses, no thyromegaly, no bruits. Chest: Lungs clear, no rales, no rhonchi, no wheezes. Heart: RR, no murmurs, no rubs, no gallops. Abdomen: Soft, no tenderness, no masses, BS normal.',
    assessmentLines: [
      'Follow-up evaluation',
      'Rash; upper chest, fungal vs irritant',
      'Constipation; medication-related',
    ],
    icdCodes: [
      { code: 'R21', description: 'Rash (acute)' },
      { code: 'K59.00', description: 'Constipation' },
    ],
    planItems: [
      'Clotrimazole 1% cream; apply to affected area as directed',
      'Keep area clean and dry; avoid excessive moisture',
      'Increase fluids and dietary fiber for constipation',
      'f/u prn',
    ],
    orderedAssessments: ['Medication Reconciliation', 'Allergy Reconciliation'],
    qualityFlags: [
      'Diagnoses reconciliation completed: Yes',
      'Medication allergy reconciliation completed: Yes',
      'Medication reconciliation completed: Yes',
      'Documentation of current medications: Yes',
    ],
  },
  {
    id: 'fm-fu-urgent-care',
    visitType: 'FM – Follow-up – Post Urgent Care (URI / Dizziness)',
    cptCode: '99213',
    patientName: 'Robert Alvarez',
    age: 58,
    sex: 'M',
    dob: '09/22/1967',
    chiefComplaint: 'f/u s/p urgent care for dizziness',
    visitDate: '12/29/2025',
    medications: [
      { name: 'Azithromycin 250 mg oral tablet (Z-Pak)', sig: 'As prescribed', status: 'sent' },
      { name: 'Albuterol HFA inhaler', sig: 'PRN', status: 'sent' },
    ],
    subjective:
      'semi and labs. Went to urgent care on 12/14/2025 for dizziness and fatigue.\nHad chest x-ray; no pneumonia.\nDx viral infection with cough.\nAs of today, still coughing and fatigued; no fever.\nSymptoms improving.',
    objective:
      'General: Normotensive, in no acute distress.\nHead: Normocephalic, no lesions.\nEyes: PERRLA, EOM\'s full, conjunctivae clear, fundi grossly normal.\nEars: EAC\'s clear, TM\'s normal.\nNose: Mucosa normal, no obstruction.\nThroat: Clear, no exudates, no lesions.\nNeck: Supple, no masses, no thyromegaly, no bruits.\nHeart: RR, no murmurs, no rubs, no gallops.\nLungs: Improved aeration; clears after coughing.',
    assessmentLines: [
      'Follow-up evaluation',
      'Viral URI; improving',
      'Cough; persistent',
    ],
    icdCodes: [
      { code: 'J06.9', description: 'Viral URI' },
      { code: 'R05', description: 'Cough' },
    ],
    planItems: [
      'Supportive care',
      'PRN medication use',
      'Complete prescribed medications',
      'Monitor symptoms; return if worsening',
    ],
    orderedAssessments: ['Medication Reconciliation', 'Chest X-ray Review'],
    qualityFlags: [
      'Diagnoses reconciliation completed: Yes',
      'Medication allergy reconciliation completed: Yes',
      'Medication reconciliation completed: Yes',
      'Documentation of current medications: Yes',
    ],
  },
  {
    id: 'fm-fu-core',
    visitType: 'FM – Follow-up – CORE (Labs / Social / Preventive)',
    cptCode: '99213',
    patientName: 'Maria Johnson',
    age: 45,
    sex: 'F',
    dob: '11/03/1980',
    chiefComplaint: 'Follow-up visit for labs and preventive care',
    visitDate: '12/29/2025',
    medications: [],
    subjective:
      'Patient presents for follow-up visit and labs.\nReports weight gain.\nCurrently taking all prescribed medications.\nFasting today.\nDue for urology and cardiology follow-up.\nHousing instability noted.',
    objective:
      'General: Normotensive, in no acute distress.\nHead: Normocephalic, no lesions.\nEyes: PERRLA, EOM\'s full, conjunctivae clear, fundi grossly normal.\nEars: EAC\'s clear, TM\'s normal.\nNose: Mucosa normal, no obstruction.\nThroat: Clear, no exudates, no lesions.\nNeck: Supple, no masses, no thyromegaly, no bruits.\nChest: Lungs clear, no rales, no rhonchi, no wheezes.\nHeart: RR, no murmurs, no rubs, no gallops.\nAbdomen: Soft, no tenderness, no masses, BS normal.',
    assessmentLines: [
      'Weight gain',
      'Housing instability',
      'Chronic medical conditions – stable',
      'Preventive care needs',
    ],
    icdCodes: [
      { code: 'R63.5', description: 'Weight gain' },
      { code: 'Z59.9', description: 'Housing instability' },
      { code: 'Z00.00', description: 'General medical exam / preventive care' },
    ],
    planItems: [
      'Labs ordered',
      'Preventive immunization administered',
      'Advised follow-up with specialists',
      'Discussed lifestyle and nutrition within current social constraints',
      'Continue current medications',
    ],
    orderedAssessments: ['Labs Review', 'BMI Screening', 'SDOH Screening'],
    qualityFlags: [
      'Diagnoses reconciliation completed: Yes',
      'Medication allergy reconciliation completed: Yes',
      'Medication reconciliation completed: Yes',
      'Documentation of current medications: Yes',
    ],
  },
  {
    id: 'fm-well-annual',
    visitType: 'FM – Well Visit – Annual Physical',
    cptCode: '99395',
    patientName: 'Ashley Chen',
    age: 25,
    sex: 'F',
    dob: '04/17/2000',
    chiefComplaint: 'Annual wellness visit',
    visitDate: '12/29/2025',
    medications: [
      { name: 'Cholecalciferol (Vitamin D3) 5000 IU', sig: 'PO daily', status: 'active' },
      { name: 'Multivitamin', sig: 'PO daily', status: 'active' },
      { name: 'Tretinoin cream', sig: 'As directed', status: 'active' },
    ],
    subjective:
      'Patient presents for annual wellness visit.\nNo acute concerns.\nTaking vitamin D and multivitamin.\nLMP regular.\nNot sexually active.',
    objective:
      'General: Normotensive, in no acute distress.\nHead: Normocephalic, no lesions.\nEyes: PERRLA, EOM\'s full, conjunctivae clear, fundi grossly normal.\nEars: EAC\'s clear, TM\'s normal.\nNose: Mucosa normal, no obstruction.\nThroat: Clear, no exudates, no lesions.\nNeck: Supple, no masses, no thyromegaly, no bruits.\nChest: Lungs clear, no rales, no rhonchi, no wheezes.\nHeart: RR, no murmurs, no rubs, no gallops.\nAbdomen: Soft, no tenderness, no masses, BS normal.',
    assessmentLines: [
      'Well visit – routine care',
    ],
    icdCodes: [
      { code: 'Z00.00', description: 'General adult medical exam without abnormal findings' },
    ],
    planItems: [
      'Labs',
      'c/w vitamin D3 5000 IU daily',
      'Counseled on diet and lifestyle modifications',
    ],
    orderedAssessments: ['PHQ-2', 'BMI', 'Cervical Cancer Screening (Pap)', 'Tobacco Screening'],
    qualityFlags: [
      'Diagnoses reconciliation completed: Yes',
      'Medication allergy reconciliation completed: Yes',
      'Medication reconciliation completed: Yes',
      'Documentation of current medications: Yes',
    ],
    screening: [
      { name: 'PHQ-2', result: 'Performed — Q1: Not at all, Q2: Not at all', status: 'completed' },
      { name: 'Body mass index (BMI) [Ratio]', result: 'Performed — Negative measurement finding', status: 'completed' },
      { name: 'Cervical cancer screening (Pap)', result: 'Recommended (VisitYear − LastPapYear ≥ 3)', status: 'recommended' },
      { name: 'Social History → Tobacco use', result: 'Non-smoker, date updated to today', status: 'completed' },
    ],
  },
];
